import { makeStyles } from 'tss-react/mui';
import {
  P2GBox,
  P2GBullet,
  P2GBulletItem,
  P2GDialog,
  P2GGrid,
  P2GLink,
  P2GText,
  useP2GMediaQueryDown
} from '@parcel2go/core-ui';

import { lazy, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { toDisplayCurrency } from '~/lib/prices';
import { Currency, Price } from '~/lib/types/basket-types';
import CTAButton from '~/components/core/button/cta-button';
import { useTaxName } from '@parcel2go/npm-p2g-app';

const CmsContent = lazy(() => import('~/components/core/content/cms-content'));

const useStyles = makeStyles()(() => ({
  root: {
    '.MuiDialog-paperFullWidth': {
      width: '100% !important'
    }
  }
}));

function BasketProtectionDialog({
  open,
  onClose,
  protection,
  onContinue,
  onUpgrade
}: {
  open: boolean;
  onClose: () => void;
  protection: { protection: number; value: number; price: Price; currency: Currency | null };
  onUpgrade: () => void;
  onContinue: () => void;
}) {
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);
  const taxName = useTaxName();
  const isSm = useP2GMediaQueryDown('sm');

  const content = useMemo(() => {
    return (
      <P2GBox p={3}>
        <P2GGrid container spacing={1} direction="column">
          <P2GGrid item>
            <P2GText variant="h4">
              {t('PROTECTION_UPGRADE_DIALOG_TITLE', {
                defaultValue: "You Haven't Fully Protected Your Parcel"
              })}
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="h5">
              {t('PROTECTION_UPGRADE_DIALOG_MESSAGE', {
                defaultValue:
                  'To increase the protection to the full value for your order it will only cost you {{value}} exc {{taxName}} extra.',
                value: toDisplayCurrency(protection.currency!, protection.price.net),
                taxName: taxName
              })}
            </P2GText>
          </P2GGrid>

          <P2GGrid item>
            <P2GText variant="subtitle1">
              {t('PROTECTION_UPGRADE_DIALOG_AMOUNT_TITLE', {
                defaultValue: '1. Amount of Protection'
              })}
            </P2GText>
          </P2GGrid>

          <P2GGrid item>
            <P2GText variant="body2">
              {t('PROTECTION_UPGRADE_DIALOG_AMOUNT_MESSAGE', {
                defaultValue:
                  "Your order is currently only covered for the value of {{protection}}, even though your order’s total declared value is {{value}}. We strongly recommend that you have full protection cover in case of loss or damage. Also please note that you are responsible for certifying the true value of the parcel's contents.",
                protection: toDisplayCurrency(protection.currency!, protection.protection),
                value: toDisplayCurrency(protection.currency!, protection.value)
              })}
            </P2GText>
          </P2GGrid>

          <CmsContent contentKey="basket-protection-dialog">
            <Trans t={t} i18nKey="PROTECTION_UPGRADE_DIALOG_CONTENT">
              <P2GGrid item>
                <P2GText variant="subtitle1">2. Prohibited / Special condition / Contents List</P2GText>
              </P2GGrid>

              <P2GGrid item>
                <P2GText variant="body2">
                  Our full terms and conditions apply, please ensure you read them carefully. Without replacing our full
                  terms we, draw your attention specifically to the following:-
                </P2GText>
              </P2GGrid>

              <P2GGrid item>
                <P2GBullet>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      You will ensure that all parcels are packaged safely and securely in a manner appropriate for the
                      item being sent
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      You will ensure that all parcels are correctly and clearly labeled
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      In order to keep our prices as low as possible, we will limit our liability in respect of lost or
                      damaged parcels to the amounts set out and agreed in our terms and conditions
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      If your package is a value higher than the protection selected, you will take out appropriate
                      extra protection to include the extra value OR take the risk of loss or damage
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      You will notify us of any potential claims against us as soon as possible and no later than 14
                      days (damage) and 28 days (for loss)
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      In the event of a potential claim we will require proof of value (excluding any expected profit –
                      which we exclude from liability) and photographs of packaging and any alleged damage
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      If we pay out on a successful claim we retain the right to the contents of the parcel to deal with
                      (sell or destroy for example) as we, in our sole discretion, see fit.
                    </P2GText>
                  </P2GBulletItem>
                </P2GBullet>
              </P2GGrid>

              <P2GGrid item>
                <P2GText variant="body2">
                  By continuing without opting for full Parcel Protection, you acknowledge and agree that:
                </P2GText>
              </P2GGrid>

              <P2GGrid item>
                <P2GBullet>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      You understand and are aware of the limit of our liability for the parcel
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      You have checked the article against the Prohibited List and the No Protection Items List
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      The prohibited list is only indicative. If you have an item that is similar to or could be
                      confused with an item on the list then you should check with us before booking the item.
                    </P2GText>
                  </P2GBulletItem>
                  <P2GBulletItem>
                    <P2GText variant="body2">
                      You accept that the responsibility for checking your item against these lists is yours and yours
                      alone
                    </P2GText>
                  </P2GBulletItem>
                </P2GBullet>
              </P2GGrid>
            </Trans>
          </CmsContent>
        </P2GGrid>
      </P2GBox>
    );
  }, [protection.currency, protection.price.net, protection.protection, protection.value, t]);

  const actions = useMemo(() => {
    return [
      <P2GGrid container justify="flex-end" spacing={2} alignItems="center" direction={isSm ? 'column' : 'row'}>
        <P2GGrid item>
          <P2GLink key="continue" onClick={onContinue} variant="primary">
            <P2GText variant="caption">
              {t('PROTECTION_UPGRADE_DIALOG_ACTION_NO', {
                defaultValue: "No, I don't want to be protected against loss or damage"
              })}
            </P2GText>
          </P2GLink>
        </P2GGrid>
        <P2GGrid item>
          <CTAButton
            loading={false}
            fullWidth={false}
            key="upgrade"
            onClick={onUpgrade}
            defaultLabel={t('PROTECTION_UPGRADE_DIALOG_ACTION_YES', { defaultValue: 'Yes, protect my parcel' })}
          ></CTAButton>
        </P2GGrid>
      </P2GGrid>
    ];
  }, [onContinue, t, onUpgrade, isSm]);

  return (
    <P2GDialog
      className={classes.root}
      open={open}
      onClose={onClose}
      title={''}
      content={content}
      actions={actions}
      maxWidth="lg"
      fullWidth={true}
    ></P2GDialog>
  );
}

export default BasketProtectionDialog;
